import React, { useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import ScheduleOutlined from '@mui/icons-material/ScheduleOutlined';
// import AssessmentOutlined from '@mui/icons-material/AssessmentOutlined';
import { motion } from 'framer-motion';
import FlowExecutionHistory from './FlowExecution';
import Sidebar from './Sidebar';
import { useDispatch, useSelector } from 'react-redux';
import { fetchWorkflows } from '../../state/slices/workflowSlice';
import { fetchProjects } from '../../state/slices/projectSlice';
import personImg from '../../Images/person.png';
import workflowsImg from '../../Images/workflows.png';
import activeFlowsImg from '../../Images/active_flows.png';
import connector from '../../Images/Connector.png';
import {
  fetchConnections,
  selectConnectionsCount,
  selectConnectionsLoading,
} from '../../state/slices/connectionsSlice';
import {
  fetchWorkflowRunHistoryThunk,
  fetchFlowExecutionHistoryThunk,
} from '../../state/slices/dashboardSlice';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import axiosInstance from '../../apis/axiosInstance';
import salesforceImg from '../../workflows/images/salesforce.png';
import oracleImg from '../../workflows/images/oracle.png';
import hubspotImg from '../../workflows/images/hubspot.png';
import sqlImg from '../../workflows/images/sql.png';
import githubImg from '../../workflows/images/github.png';
import jiraImg from '../../workflows/images/JIRA.svg';

const StatCardSkeleton = () => (
  <motion.div
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.3 }}
  >
    <Paper
      elevation={0}
      sx={{
        borderRadius: '16px',
        py: 3,
        px: 2.5,
        background: 'linear-gradient(135deg, #ffffff 0%, #f8faff 100%)',
        border: '1px solid rgba(14, 15, 15, 0.1)',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        minHeight: 60,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
        },
      }}
    >
      <Skeleton
        variant="circular"
        width={48}
        height={48}
        sx={{
          background:
            'linear-gradient(90deg, #f0f0f0 25%, transparent 50%, #f0f0f0 75%)',
        }}
      />
      <Box sx={{ width: '100%' }}>
        <Skeleton variant="text" width={80} height={24} sx={{ mb: 1 }} />
        <Skeleton variant="text" width={60} height={20} />
      </Box>
    </Paper>
  </motion.div>
);

export const DashboardLayout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const workflows = useSelector((state) => state.workflows.list);
  const workflowLoading = useSelector((state) => state.workflows.loading);

  const projects = useSelector((state) => state.projects.list);
  const projectLoading = useSelector((state) => state.projects.loading);
  const projectCount = projects.length;

  const connectionsCount = useSelector(selectConnectionsCount);
  const connectionsloading = useSelector(selectConnectionsLoading);

  // Dashboard data for Schedule Job and Reports counts
  const {
    workflowRunHistory = [],
    // flowExecutionData = [],
  } = useSelector((state) => state.dashboard);

  const [connectorsOpen, setConnectorsOpen] = React.useState(false);

  const handleConnectorsOpen = () => setConnectorsOpen(true);
  const handleConnectorsClose = () => setConnectorsOpen(false);



  useEffect(() => {
    dispatch(fetchProjects());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchConnections());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchWorkflows())
      .unwrap()
      .then((data) => {
        console.log('[DEBUG] fetchWorkflows success:', data);
      })
      .catch((err) => {
        console.error('[ERROR] fetchWorkflows failed:', err);
      });
  }, [dispatch]);

  // Fetch workflow run history for Schedule Job and Reports counts
  useEffect(() => {
    dispatch(fetchWorkflowRunHistoryThunk({ range: 'weekly', limit: 100 }));
    dispatch(fetchFlowExecutionHistoryThunk({ limit: 1000 }));
  }, [dispatch]);

  useEffect(() => {
    const refreshTrialStatus = async () => {
      try {
        const token = Cookies.get('authToken');
        if (!token) return;

        const response = await axiosInstance.get('/auth/user', {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.trial_status) {
          Cookies.set('trialStatus', JSON.stringify(response.data.trial_status), { 
            expires: 1, 
            secure: true 
          });
        }
      } catch (error) {
        console.error('Failed to refresh trial status:', error);
      }
    };

    refreshTrialStatus();
  }, []);

  const userWorkflows = workflows;

  // Calculate Schedule Job count (workflows with schedule enabled)
  const scheduledJobCount = useMemo(() => {
    if (!Array.isArray(workflowRunHistory)) return 0;
    const scheduled = workflowRunHistory.filter((run) => !!run?.is_scheduled);
    // Get unique workflow IDs
    const uniqueWorkflowIds = [...new Set(scheduled.map(run => run.workflow_id))];
    return uniqueWorkflowIds.length;
  }, [workflowRunHistory]);

  // Calculate Reports count (total workflow runs)
  // const reportsCount = useMemo(() => {
  //   if (!Array.isArray(flowExecutionData)) return 0;
  //   return flowExecutionData.length;
  // }, [flowExecutionData]);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut',
      },
    },
  };

  const cardHoverVariants = {
    hover: {
      scale: 1.03,
      y: -2,
      transition: {
        duration: 0.2,
        ease: 'easeInOut',
      },
    },
  };

  return (
    <motion.div variants={containerVariants} initial="hidden" animate="visible">
      <Box
        sx={{
          display: 'flex',
          minHeight: '100vh',
          background: '#f8f9fa',
        }}
      >
        <Sidebar />

        <Box
          sx={{
            paddingTop: { xs: '0.5rem', sm: '0.75rem', md: '1rem' },
            paddingRight: { xs: '1rem', sm: '2rem', md: '3rem' },
            paddingLeft: { xs: '1rem', sm: '1.5rem', md: '2rem' },
            width: '100%',
            maxWidth: '100%',
            overflow: 'hidden',
          }}
        >
          {/* <motion.div variants={itemVariants}>
            <Box
              sx={{
                mb: 1.5,
                background: '#5965A6',
                borderRadius: '8px',
                py: { xs: 0.75, sm: 2 },
                px: { xs: 2, sm: 3 },
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: { xs: 1, sm: 0 },
                width: '97%',
              }}
            >
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <img
                    src={RocketIcon}
                    alt="Rocket"
                    style={{
                      width: 'clamp(2rem, 3vw, 3rem)',
                      height: 'clamp(2rem, 3vw, 3rem)'
                    }}
                  />
                  <Box>
                    <Typography
                      sx={{
                        fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
                        fontWeight: 700,
                        color: 'white',
                        mb: 0.25,
                        lineHeight: 1.1,
                      }}
                    >
                      Get Started With Our New Features!
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.9rem' },
                        fontWeight: 400,
                        color: 'rgba(255, 255, 255, 0.9)',
                        lineHeight: 1.2,
                        mt: 1
                      }}
                    >
                      Explore our latest tools and enhancements
                    </Typography>
                  </Box>
                </Box>

              </Box>
              <Button
                sx={{
                  backgroundColor: 'white',
                  color: '#1976d2',
                  px: 2,
                  py: 1,
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 700,

                  '&:hover': {
                    backgroundColor: '#1976d2',
                    color: 'white',
                  },
                }}
              >
                Learn More
                <ArrowRightIcon sx={{ fontSize: '1rem', ml: 1 }} />
              </Button>
            </Box>
          </motion.div> */}


{/* Combined Row: Total Projects, Connections, Connector, Workflows, Scheduled Jobs */}
<motion.div variants={itemVariants}>
  <Grid
    container
    spacing={2}
    sx={{ mb: 3,mt:1.5 }}
    // 1 card / row on xs, 2 on sm, 5 on md+
    columns={{ xs: 4, sm: 8, md: 20 }}
  >
    {[
      // --- from First Row ---
      {
        icon: (
          <Box
            component="img"
            src={personImg}
            alt="User"
            sx={{ width: 28, height: 28 }}
          />
        ),
        value: projectCount.toString().padStart(2, '0'),
        label: 'Total Projects',
        loading: projectLoading,
        bgColor: '#EEF2FF',
        path: '/recentworkflows',
      },
      {
        icon: (
          <Box
            component="img"
            src={activeFlowsImg}
            alt="Connections"
            sx={{ width: 28, height: 28 }}
          />
        ),
        value: connectionsCount.toString().padStart(2, '0'),
        label: 'Connections',
        loading: connectionsloading,
        bgColor: '#ECFDF5',
        path: '/myconnections',
      },
      {
        icon: (
          <Box
            component="img"
            src={connector}
            alt="Connector"
            sx={{ width: 28, height: 28 }}
          />
        ),
        value: '04',
        label: 'Connector',
        loading: false,
        bgColor: '#FFE8E8',
        path: '/connectors',
      },

      // --- from Second Row ---
      {
        icon: (
          <Box
            component="img"
            src={workflowsImg}
            alt="Workflows"
            sx={{ width: 28, height: 28 }}
          />
        ),
        value: userWorkflows.length.toString().padStart(2, '0'),
        label: 'Workflows',
        loading: workflowLoading,
        bgColor: '#FFFBEB',
        path: '/recentworkflows',
      },
      {
        icon: <ScheduleOutlined sx={{ fontSize: 28 }} />,
        value: scheduledJobCount.toString().padStart(2, '0'),
        label: 'Scheduled Jobs',
        loading: false,
        bgColor: '#E0E7FF',
        path: '/schedule-job',
        iconColor: '#5965A6', // same as before
      },
      // If you later add "Reports" or others, just push here.
    ].map((card, index) => (
      <Grid item xs={4} sm={4} md={4} key={card.label}>
        <motion.div
          variants={cardHoverVariants}
          whileHover="hover"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1, duration: 0.4 }}
        >
          {card.loading ? (
            <StatCardSkeleton />
          ) : (
            <Paper
              elevation={0}
              onClick={() => {
                // keep existing behaviours exactly
                if (card.label === 'Connector') {
                  handleConnectorsOpen();
                } else if (card.path) {
                  navigate(card.path);
                }
              }}
              sx={{
                borderRadius: '8px',
                p: { xs: 1.5, sm: 2 },
                border: '1px solid #e5e7eb',
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 1.25, sm: 1.5 },
                minHeight: { xs: 30, sm: 40 },
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                boxShadow: '0 3px 10px rgba(0, 0, 0, 0.1)',
                background: '#ffffff',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                },
              }}
            >
              <Box
                sx={{
                  width: { xs: 36, sm: 42 },
                  height: { xs: 36, sm: 42 },
                  borderRadius: '8px',
                  background: card.bgColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  color: card.iconColor || 'inherit',
                }}
              >
                {card.icon}
              </Box>
              <Box sx={{ flex: 1, textAlign: 'left' }}>
                <Typography
                  sx={{
                    fontSize: {
                      xs: '1.2rem',
                      sm: '1.5rem',
                      md: '1.7rem',
                    },
                    fontWeight: 700,
                    color: '#1a1a1a',
                    lineHeight: 1,
                    mb: 0.25,
                  }}
                >
                  {card.value}
                </Typography>
                <Typography
                  sx={{
                    color: '#6b7280',
                    fontSize: { xs: '0.75rem', sm: '0.85rem' },
                    fontWeight: 500,
                    lineHeight: 1.2,
                  }}
                >
                  {card.label}
                </Typography>
              </Box>
            </Paper>
          )}
        </motion.div>
      </Grid>
    ))}
  </Grid>
</motion.div>



          {/* Flow Execution History Section */}
          <Grid container spacing={2} alignItems="stretch">


            {/* Enhanced Flow Execution History */}
            <Grid item xs={12}>
              <motion.div
                variants={itemVariants}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.6 }}
              >
                <FlowExecutionHistory />
              </motion.div>
            </Grid>
          </Grid>
        </Box>

        {/* Connectors Dialog */}
        <Dialog
          open={connectorsOpen}
          onClose={handleConnectorsClose}
          maxWidth="sm"
          fullWidth
          BackdropProps={{
            sx: {
              backgroundColor: 'rgba(179, 179, 179, 0.35)',
              backdropFilter: 'blur(1px)',
            },
          }}
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Connectors
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {[
                { name: 'Salesforce', img: salesforceImg },
                { name: 'Oracle', img: oracleImg },
                { name: 'HubSpot', img: hubspotImg },
                { name: 'SQL', img: sqlImg },
                { name: 'GitHub', img: githubImg },
                { name: 'Jira', img: jiraImg },
              ].map((conn) => (
                <Grid item xs={12} sm={6} key={conn.name}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: '10px',
                      border: '1px solid #e5e7eb',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                    }}
                  >
                    <Box component="img" src={conn.img} alt={conn.name} sx={{ width: 32, height: 32 }} />
                    <Typography>{conn.name}</Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </DialogContent>
        </Dialog>
      </Box>
    </motion.div>
  );
};

export default DashboardLayout;
